import React, { createContext, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChevronDown } from 'lucide-react';
import { cn } from 'cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ContextMenu as UIContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { ButtonGroup } from '@/components/ui/button-group';
import Button from '../button';

type Direction = 'up' | 'down' | 'left' | 'right';

const SIDES: Record<Direction, 'top' | 'bottom' | 'left' | 'right'> = {
  up: 'top',
  down: 'bottom',
  left: 'left',
  right: 'right',
};

const DirectionContext = createContext<Direction>('down');

// Os mesmos DropdownItem/DropdownSubmenu servem ao DropdownMenu e ao ContextMenu
// (ex.: o DropdownItemList aparece nos dois). Cada menu do Radix tem o próprio
// contexto, então o item renderiza a peça do menu em que está.
const MenuKindContext = createContext<'dropdown' | 'context'>('dropdown');

type UncontrolledDropdownProps = {
  /** Lado do gatilho em que o menu abre. */
  direction?: Direction;
  /** Põe o gatilho em linha (`inline-flex`), como o `.btn-group`. Sem ele, o envoltório é um bloco. */
  group?: boolean;
  /** Não abre o menu. */
  disabled?: boolean;
  /** Classes do envoltório. */
  className?: string;
  /** Estilo do envoltório. */
  style?: React.CSSProperties;
  /** Id do envoltório. */
  id?: string;
  /** O `DropdownToggle` e o `DropdownMenu`. */
  children?: React.ReactNode;
};

// modal={false}: o modal do Radix põe pointer-events: none no body e trava a
// rolagem enquanto o menu está aberto, o que o dropdown do reactstrap não fazia.
// Estado controlado aqui: com o open do próprio Radix, um clique no gatilho
// desabilitado ficava guardado e o menu abria sozinho quando o disabled saía.
function Root({
  direction = 'down',
  disabled,
  children,
}: Pick<UncontrolledDropdownProps, 'direction' | 'disabled' | 'children'>) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  return (
    <DirectionContext.Provider value={direction}>
      <DropdownMenu
        modal={false}
        open={open && !disabled}
        onOpenChange={(value) => !disabled && setOpen(value)}
      >
        {children}
      </DropdownMenu>
    </DirectionContext.Provider>
  );
}

/**
 * Menu que abre ao clicar no gatilho. Dentro dele vão um `DropdownToggle` e um `DropdownMenu` com os `DropdownItem`.
 * @import import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'src/componentes/dropdown';
 */
function UncontrolledDropdown({
  group,
  className,
  style,
  id,
  ...rootProps
}: UncontrolledDropdownProps) {
  return (
    <Root {...rootProps}>
      <div
        id={id}
        style={style}
        className={cn(
          'tw:relative',
          group && 'tw:inline-flex tw:align-middle',
          className
        )}
      >
        {rootProps.children}
      </div>
    </Root>
  );
}

type UncontrolledButtonDropdownProps = Omit<UncontrolledDropdownProps, 'group'>;

/**
 * Botão dividido: um `Button` com a ação principal e, ao lado, um `DropdownToggle split` que abre as outras.
 * @import import { UncontrolledButtonDropdown } from 'src/componentes/dropdown';
 */
function UncontrolledButtonDropdown({
  className,
  style,
  id,
  ...rootProps
}: UncontrolledButtonDropdownProps) {
  return (
    <Root {...rootProps}>
      <ButtonGroup
        id={id}
        style={style}
        className={cn('tw:inline-flex tw:align-middle', className)}
      >
        {rootProps.children}
      </ButtonGroup>
    </Root>
  );
}

type DropdownToggleProps = React.ComponentProps<typeof Button> & {
  /** Seta para baixo depois do texto. */
  caret?: boolean;
  /** Parte da seta de um botão dividido (dentro do `UncontrolledButtonDropdown`): mostra só a seta. */
  split?: boolean;
  /** O filho é o gatilho, sem a aparência de botão (ex.: o nome do item como link). O filho precisa aceitar ref. */
  asChild?: boolean;
};

/**
 * Gatilho do menu. É o `Button` do portal (mesmas props), com `color="secondary"` e tamanho de ícone por padrão. Só ícone: `toolbar icon="..."`. Com `split` ou só ícone, sai com `aria-label` "Mais opções", a menos que a tela passe outro. Para um link ou texto como gatilho, use `asChild`.
 * @import import { DropdownToggle } from 'src/componentes/dropdown';
 */
const DropdownToggle = React.forwardRef<HTMLButtonElement, DropdownToggleProps>(
  function DropdownToggle(
    {
      color = 'secondary',
      caret,
      split,
      toolbar,
      size = caret || toolbar ? undefined : 'icon',
      disabled,
      asChild,
      className,
      children,
      ...props
    },
    ref
  ) {
    const intl = useIntl();
    const iconOnly = split || (toolbar && !children);

    // Sem o data-slot do shadcn: o filho é marcação da tela, e o reset do
    // tailwind-base.css ([data-slot]) zeraria o padding e a cor do link dele.
    if (asChild)
      return (
        <DropdownMenuTrigger
          asChild
          data-slot={undefined}
          disabled={disabled}
          className={className}
          {...(props as React.HTMLAttributes<HTMLElement>)}
        >
          {children}
        </DropdownMenuTrigger>
      );

    // O disabled vai também para o Trigger: o Radix só confere o dele para não abrir.
    return (
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          ref={ref}
          color={color}
          size={size}
          toolbar={toolbar}
          disabled={disabled}
          aria-label={
            iconOnly ? intl.formatMessage({ id: 'labelMaisOpcoes' }) : undefined
          }
          className={cn(split && 'tw:ml-px tw:w-7', className)}
          {...props}
        >
          {split ? <ChevronDown strokeWidth={1.75} /> : children}
          {caret && <ChevronDown strokeWidth={1.75} />}
        </Button>
      </DropdownMenuTrigger>
    );
  }
);

type DropdownMenuProps = {
  /** Alinha o menu pela borda final do gatilho (direita), em vez da inicial. */
  end?: boolean;
  /** Classes do menu (ex.: largura). */
  className?: string;
  /** Estilo do menu. */
  style?: React.CSSProperties;
  /** Os `DropdownItem` e `DropdownSubmenu`. */
  children?: React.ReactNode;
};

/**
 * Lista do menu, aberta em portal no `body`. O lado vem do `direction` do `UncontrolledDropdown`.
 * @import import { DropdownMenu } from 'src/componentes/dropdown';
 */
function DropdownMenuComponent({
  end,
  className,
  style,
  children,
}: DropdownMenuProps) {
  const direction = useContext(DirectionContext);

  return (
    <DropdownMenuContent
      side={SIDES[direction]}
      align={end ? 'end' : 'start'}
      className={className}
      style={style}
    >
      <MenuKindContext.Provider value="dropdown">
        {children}
      </MenuKindContext.Provider>
    </DropdownMenuContent>
  );
}

const PARTS = {
  dropdown: {
    Item: DropdownMenuItem,
    Separator: DropdownMenuSeparator,
    Label: DropdownMenuLabel,
    Sub: DropdownMenuSub,
    SubTrigger: DropdownMenuSubTrigger,
    SubContent: DropdownMenuSubContent,
  },
  context: {
    Item: ContextMenuItem,
    Separator: ContextMenuSeparator,
    Label: ContextMenuLabel,
    Sub: ContextMenuSub,
    SubTrigger: ContextMenuSubTrigger,
    SubContent: ContextMenuSubContent,
  },
};

type DropdownItemProps = Omit<
  React.ComponentPropsWithoutRef<typeof DropdownMenuItem>,
  'onSelect'
> & {
  /** Linha separadora no lugar do item. */
  divider?: boolean;
  /** Título de seção, sem clique. */
  header?: boolean;
  /** `false` mantém o menu aberto depois do clique. */
  toggle?: boolean;
};

/**
 * Item do menu (do `DropdownMenu` ou do `ContextMenu`). O clique vai no `onClick`, que também dispara com Enter e Espaço. Ícone e texto vão como filhos, sem `className="mr-2"` no ícone (o espaço é do item). Para link, `asChild` com o `<a href>` como filho.
 * @import import { DropdownItem } from 'src/componentes/dropdown';
 */
const DropdownItem = React.forwardRef<HTMLDivElement, DropdownItemProps>(
  function DropdownItem(
    { divider, header, toggle = true, className, style, children, ...props },
    ref
  ) {
    const { Item, Separator, Label } = PARTS[useContext(MenuKindContext)];

    if (divider) return <Separator className={className} style={style} />;

    if (header)
      return (
        <Label className={className} style={style}>
          {children}
        </Label>
      );

    // Link como item (asChild): o a:hover do modern.css (cor e sublinhado com
    // !important) só é anulado para o .dropdown-item do Bootstrap.
    return (
      <Item
        ref={ref}
        className={cn(
          props.asChild &&
            'tw:hover:text-accent-foreground! tw:hover:no-underline!',
          className
        )}
        style={style}
        onSelect={toggle ? undefined : (event) => event.preventDefault()}
        {...props}
      >
        {children}
      </Item>
    );
  }
);

type DropdownSubmenuProps = {
  /** Texto do item que abre o submenu. */
  title: React.ReactNode;
  /** Os `DropdownItem` do submenu. */
  children?: React.ReactNode;
};

/**
 * Item que abre um submenu ao lado, ao passar o mouse ou com a seta para a direita. O lado troca sozinho quando não cabe.
 * @import import { DropdownSubmenu } from 'src/componentes/dropdown';
 */
function DropdownSubmenu({ title, children }: DropdownSubmenuProps) {
  const { Sub, SubTrigger, SubContent } = PARTS[useContext(MenuKindContext)];
  const [offset, setOffset] = useState(0);

  // O Radix abre o submenu colado no item; com o padding e a barra de rolagem
  // do menu pai, ele ficaria por cima dela. Mede até a borda do menu pai.
  const measure = (event: React.SyntheticEvent<HTMLElement>) => {
    const trigger = event.currentTarget.getBoundingClientRect();
    const menu = event.currentTarget
      .closest('[role=menu]')
      ?.getBoundingClientRect();
    if (menu) setOffset(Math.max(0, menu.right - trigger.right));
  };

  return (
    <Sub>
      <SubTrigger onPointerEnter={measure} onFocus={measure}>
        {title}
      </SubTrigger>
      <SubContent sideOffset={offset} alignOffset={-5}>
        {children}
      </SubContent>
    </Sub>
  );
}

type ContextMenuProps = {
  /** Os `DropdownItem` e `DropdownSubmenu` do menu. */
  content: React.ReactNode;
  /** Não abre o menu. O clique direito segue para cima: abre o `ContextMenu` de fora, se houver, ou o menu do navegador. */
  disabled?: boolean;
  /** Classes do menu (ex.: largura). */
  className?: string;
  /** Chamado ao abrir e ao fechar, com o novo estado. */
  onOpenChange?: (open: boolean) => void;
  /** A área que abre o menu no clique direito: um único elemento que aceite ref (ex.: `div`). Não pode ter `onContextMenu` com `preventDefault`. */
  children: React.ReactElement;
};

/**
 * Menu que abre com o clique direito, na posição do cursor (ex.: ações de um widget de dashboard). Os itens são os mesmos do dropdown.
 * @import import { ContextMenu } from 'src/componentes/dropdown';
 */
function ContextMenu({
  content,
  disabled,
  className,
  onOpenChange,
  children,
}: ContextMenuProps) {
  return (
    <UIContextMenu modal={false} onOpenChange={onOpenChange}>
      {/* Sem data-slot pelo mesmo motivo do DropdownToggle asChild: o widget é da tela. */}
      <ContextMenuTrigger asChild data-slot={undefined} disabled={disabled}>
        {children}
      </ContextMenuTrigger>
      {!disabled && (
        <ContextMenuContent className={className}>
          <MenuKindContext.Provider value="context">
            {content}
          </MenuKindContext.Provider>
        </ContextMenuContent>
      )}
    </UIContextMenu>
  );
}

export {
  UncontrolledDropdown,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenuComponent as DropdownMenu,
  DropdownItem,
  DropdownSubmenu,
  ContextMenu,
};
